import React, { useState, useCallback, type ReactNode } from 'react'

export interface GalleryImage {
  id: string
  url: string
  filename: string
  createdAt?: string
}

interface ImageGalleryProps {
  images: GalleryImage[]
  emptyMessage?: ReactNode
  onImageClick?: (image: GalleryImage) => void
  columns?: 2 | 3 | 4
}

/**
 * Image Gallery Component
 *
 * Displays images in a responsive grid with:
 * - Configurable columns (2, 3, or 4)
 * - Lightbox modal for full-size viewing
 * - Keyboard navigation (arrows, escape)
 * - Loading and error states per image
 */
export default function ImageGallery({
  images,
  emptyMessage = 'No images to display',
  onImageClick,
  columns = 4,
}: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})
  const [errorStates, setErrorStates] = useState<Record<string, boolean>>({})

  const columnClasses = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
  }

  const handleImageLoad = useCallback((id: string) => {
    setLoadingStates((prev) => ({ ...prev, [id]: false }))
  }, [])

  const handleImageError = useCallback((id: string) => {
    setLoadingStates((prev) => ({ ...prev, [id]: false }))
    setErrorStates((prev) => ({ ...prev, [id]: true }))
  }, [])

  const openLightbox = useCallback((index: number) => {
    setSelectedIndex(index)
  }, [])

  const closeLightbox = useCallback(() => {
    setSelectedIndex(null)
  }, [])

  const goToPrevious = useCallback(() => {
    setSelectedIndex((prev) => {
      if (prev === null || prev === 0) return images.length - 1
      return prev - 1
    })
  }, [images.length])

  const goToNext = useCallback(() => {
    setSelectedIndex((prev) => {
      if (prev === null || prev === images.length - 1) return 0
      return prev + 1
    })
  }, [images.length])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectedIndex === null) return

      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
      }
    },
    [selectedIndex, closeLightbox, goToPrevious, goToNext]
  )

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {emptyMessage}
      </div>
    )
  }

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null

  return (
    <div onKeyDown={handleKeyDown} tabIndex={0} className="outline-none">
      {/* Grid */}
      <div className={`grid ${columnClasses[columns]} gap-4`}>
        {images.map((image, index) => (
          <button
            key={image.id}
            type="button"
            onClick={() => {
              if (onImageClick) {
                onImageClick(image)
              } else {
                openLightbox(index)
              }
            }}
            className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group focus:ring-2 focus:ring-primary-500 focus:outline-none"
          >
            {/* Loading state */}
            {loadingStates[image.id] !== false && !errorStates[image.id] && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
              </div>
            )}

            {/* Error state */}
            {errorStates[image.id] && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xs">Failed to load</span>
              </div>
            )}

            {/* Image */}
            <img
              src={image.url}
              alt={image.filename}
              className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${
                errorStates[image.id] ? 'hidden' : ''
              }`}
              onLoad={() => handleImageLoad(image.id)}
              onError={() => handleImageError(image.id)}
            />

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <svg
                className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedIndex !== null && selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            aria-label="Close lightbox"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                goToPrevious()
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-10"
              aria-label="Previous image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt={selectedImage.filename}
              className="max-w-full max-h-[90vh] object-contain"
            />

            {/* Image info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white">
              <p className="font-medium">{selectedImage.filename}</p>
              {selectedImage.createdAt && (
                <p className="text-sm text-white/70">
                  {new Date(selectedImage.createdAt).toLocaleDateString()}
                </p>
              )}
              <p className="text-sm text-white/50 mt-1">
                {selectedIndex + 1} of {images.length}
              </p>
            </div>
          </div>

          {/* Next button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-10"
              aria-label="Next image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
